<script setup lang="ts">
import type { KanbanLog } from '@entities/kanban-comment'
import type { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'

import { KanbanLogItem, KanbanLogComment, KanbanLogDeadline, KanbanLogValue, KanbanLogExtraFields, KanbanLogAssignee, KanbanLogCreator } from '@widgets/kanban-comments'
import { useKanbanLog, useKanbanLogItem } from '@entities/kanban-comment'
import { useListsStore } from '@shared/model'
import { BadgeKanbanStageStatus } from '@shared/ui/kanban'

import { TASKS_KANBAN_STAGE_COLOR } from '../config/tasks-kanban-stage-color.const'
import TasksKanbanLogTaskType from './TasksKanbanLogTaskType.vue'
import TasksKanbanLogStatus from './TasksKanbanLogStatus.vue'

defineOptions({
  name: 'TasksKanbanLogItem',
})

const props = defineProps<{
  log: KanbanLog
  main: boolean
  last: boolean
}>()

const listsStore = useListsStore()
const stageList = listsStore.getLists?.tasks_kanban?.lists?.status ?? {}

const { getFieldLabel } = useKanbanLog()

const refLog = computed<KanbanLog>(() => props.log)

const {
  refIsStatus,
  refIsTaskType,
  refIsExtraFields,
  refIsAssignee,
  refIsCreator,
  refIsDeadline,
  refIsComment,
} = useKanbanLogItem(refLog)

const refStage = computed<string | undefined>(() => props.log.task_status ?? props.log.stage)
</script>

<template>
  <KanbanLogItem
    :log="log"
    :main="main"
    :last="last"
  >
    <template
      v-if="refStage"
      #stage
    >
      <BadgeKanbanStageStatus
        :text="stageList[refStage] ?? refStage"
        :color="TASKS_KANBAN_STAGE_COLOR[refStage as TasksKanbanStage] ?? 'gray'"
      />
    </template>
    <TasksKanbanLogStatus
      v-if="refIsStatus"
      :actor="log.actor"
      :old-value="log.old_value"
      :value="log.value"
    />
    <TasksKanbanLogTaskType
      v-else-if="refIsTaskType"
      :actor="log.actor"
      :old-value="log.old_value"
      :value="log.value"
    />
    <KanbanLogExtraFields
      v-else-if="refIsExtraFields"
      :actor="log.actor"
      :old-value="log.old_value"
      :value="log.value"
    />
    <KanbanLogAssignee
      v-else-if="refIsAssignee"
      :actor="log.actor"
      :old-assignee="log.old_assignee"
      :new-assignee="log.new_assignee"
    />
    <KanbanLogCreator
      v-else-if="refIsCreator"
      :actor="log.actor"
      :creator="log.creator"
    />
    <KanbanLogDeadline
      v-else-if="refIsDeadline"
      :actor="log.actor"
      :old-value="log.old_value"
      :value="log.value"
    />
    <KanbanLogComment
      v-else-if="refIsComment"
      :actor="log.actor"
      :action="log.action"
      :value="log.value"
    />
    <KanbanLogValue
      v-else-if="log.field"
      :actor="log.actor"
      :label="getFieldLabel(log.field)"
      :old-value="log.old_value"
      :value="log.value"
    />
  </KanbanLogItem>
</template>